import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Search, TrendingUp, Filter, ExternalLink, Plus } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { marketsApi } from '../lib/api'
import { formatCurrency, formatDate } from '../lib/utils'
import { useAuthStore } from '../store/authStore'

export function Markets() {
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const { isHydrated } = useAuthStore()

  const { data: markets, isLoading } = useQuery({
    queryKey: ['markets'],
    queryFn: () => marketsApi.getAll().then(r => r.data),
    enabled: isHydrated,
  })

  const trackMutation = useMutation({
    mutationFn: (id: string) => marketsApi.track(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['markets'] }),
  })

  const filteredMarkets = markets?.filter((market: any) => {
    const matchesSearch = market.question.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = !categoryFilter || market.category === categoryFilter
    return matchesSearch && matchesCategory
  })

  const categories = [...new Set(markets?.map((m: any) => m.category) || [])]

  if (isLoading) {
    return <div className="text-center py-12">Loading markets...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Markets</h1>
          <p className="mt-1 text-gray-500">Discover and track prediction markets</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
            placeholder="Search markets..."
          />
        </div>

        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="pl-10 pr-8 py-2 border rounded-lg appearance-none bg-white"
          >
            <option value="">All Categories</option>
            {categories.map((cat: string) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Markets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredMarkets?.map((market: any) => (
          <Link
            key={market.id}
            to={`/markets/${market.id}`}
            className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {market.category}
              </span>
              {market.isTracked && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Tracked
                </span>
              )}
            </div>

            <h3 className="font-semibold text-gray-900 mb-4 line-clamp-2">
              {market.question}
            </h3>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-xs text-gray-500">YES</p>
                <p className="text-lg font-bold text-green-600">{(market.yesPrice * 100).toFixed(1)}¢</p>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <p className="text-xs text-gray-500">NO</p>
                <p className="text-lg font-bold text-red-600">{(market.noPrice * 100).toFixed(1)}¢</p>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>Volume: {formatCurrency(market.volume || 0)}</span>
              <span>Ends {formatDate(market.endDate)}</span>
            </div>
          </Link>
        ))}

        {(!filteredMarkets || filteredMarkets.length === 0) && (
          <div className="col-span-full text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
            <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No markets found matching your criteria</p>
          </div>
        )}
      </div>
    </div>
  )
}
